import styled, { css } from 'styled-components';

/**
 * Peças visuais do assistente de configuração (out/2026 — proposta de correção
 * da configuração inicial). Os passos vivem nos modais que o cliente já
 * conhece (padrão atual: cabeçalho escuro do `Modal` e conteúdo branco); estes
 * estilos são só o miolo comum: etiqueta do passo, grade de marcas/
 * distribuidoras, opção "não aparece na lista", avisos e caixa de copiar.
 */

export const WizardBody = styled.div`
  padding: 28px 30px 8px;
  font-family: ${(props) => props.theme.font.body.family};
  color: ${(props) => props.theme.color.text.primary};

  h3 {
    font-family: ${(props) => props.theme.font.title.family};
    font-size: 20px;
    font-weight: ${(props) => props.theme.font.title.weight.bold};
    color: ${(props) => props.theme.color.text.tertiary};
    margin: 0 0 6px;
  }

  p,
  li {
    font-size: ${(props) => props.theme.font.body.size.small};
    line-height: ${(props) => props.theme.font.body.lineHeight.small};
  }

  ol {
    padding-left: 20px;
    margin-bottom: 16px;
  }

  a {
    color: ${(props) => props.theme.color.ui.orange};
    font-weight: ${(props) => props.theme.font.body.weight.bold};
  }

  @media only screen and (max-width: 600px) {
    padding: 20px 16px 4px;
  }
`;

export const StepTag = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${(props) => props.theme.color.ui.orange};
  margin-bottom: 6px;
`;

export const Lead = styled.p`
  color: ${(props) => props.theme.color.text.quaternary};
  margin-bottom: 20px;
`;

export const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;

  @media only screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const ChoiceCard = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border-radius: 3px;
  background: ${(props) => props.theme.color.bg.primary};
  border: ${(props) => (props.selected ? '2px' : '1px')} solid
    ${(props) =>
      props.selected
        ? props.theme.color.ui.orange
        : props.theme.color.ui.shade2};
  cursor: pointer;

  img {
    width: 44px;
    height: 44px;
    object-fit: contain;
    flex-shrink: 0;
  }

  strong {
    display: block;
    font-size: 15px;
    color: ${(props) => props.theme.color.text.secondary};
  }

  small {
    display: block;
    font-size: 12px;
    color: ${(props) => props.theme.color.text.quaternary};
  }

  &:hover {
    background: ${(props) => props.theme.color.ui.shade1};
  }
`;

export const UnlistedOption = styled.button`
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border: 1px dashed ${(props) => props.theme.color.ui.shade3};
  border-radius: 3px;
  background: transparent;
  font-size: 14px;
  color: ${(props) => props.theme.color.text.primary};
  cursor: pointer;

  b {
    color: ${(props) => props.theme.color.text.secondary};
  }

  span {
    color: ${(props) => props.theme.color.text.quaternary};
  }

  &:hover {
    border-color: ${(props) => props.theme.color.ui.orange};
  }
`;

const toneColors = {
  info: css`
    background: #f2f2f2;
    border-color: #e6e6e6;
  `,
  warn: css`
    background: ${(props) => props.theme.color.bg.warning};
    border-color: #f79422;
  `,
  error: css`
    background: ${(props) => props.theme.color.bg.danger};
    border-color: #f42121;
  `,
  success: css`
    background: ${(props) => props.theme.color.bg.success};
    border-color: #98c52f;
  `,
};

export const Notice = styled.div<{ tone?: keyof typeof toneColors }>`
  border: 1px solid;
  border-radius: 3px;
  padding: 10px 14px;
  margin: 12px 0;
  font-size: ${(props) => props.theme.font.body.size.small};
  line-height: ${(props) => props.theme.font.body.lineHeight.small};
  ${(props) => toneColors[props.tone ?? 'info']}

  p {
    margin: 0;
  }
`;

export const CopyBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border: 1px solid ${(props) => props.theme.color.ui.shade2};
  border-radius: 3px;
  background: ${(props) => props.theme.color.ui.shade1};
  margin: 6px 0 4px;

  code {
    flex: 1;
    min-width: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${(props) => props.theme.color.text.secondary};
    word-break: break-all;
  }
`;

export const SmallAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${(props) => props.theme.color.ui.orange};
  background: ${(props) => props.theme.color.bg.primary};
  color: ${(props) => props.theme.color.ui.orange};
  border-radius: 3px;
  font-size: 13px;
  font-weight: 700;
  padding: 5px 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;
  font-size: 13px;
  font-weight: 700;
  color: ${(props) => props.theme.color.text.tertiary};

  input,
  select,
  textarea {
    font-weight: 400;
    font-size: 14px;
    padding: 8px 10px;
    border: 1px solid ${(props) => props.theme.color.ui.shade2};
    border-radius: 3px;
    background: ${(props) => props.theme.color.bg.primary};
    color: ${(props) => props.theme.color.text.primary};
  }

  textarea {
    min-height: 70px;
    resize: vertical;
  }
`;

export const WizardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 30px 24px;

  @media only screen and (max-width: 600px) {
    padding: 12px 16px 20px;
  }
`;

export const StatusTag = styled.span<{
  tone: 'good' | 'warn' | 'bad' | 'muted';
}>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(props) =>
    props.tone === 'muted'
      ? props.theme.color.text.primary
      : props.theme.color.text.inverse};
  background: ${(props) =>
    props.tone === 'good'
      ? props.theme.color.ui.darkGreen
      : props.tone === 'warn'
      ? props.theme.color.ui.yellowAlt
      : props.tone === 'bad'
      ? props.theme.color.ui.redAlt
      : props.theme.color.ui.shade2};
`;
